import React, { useEffect, useMemo, useState } from 'react';

import { Grid } from '@material-ui/core';
import Helmet from 'react-helmet';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useHistory } from 'react-router-dom';

import { VALIDATORS_MAP } from 'components/FormComponents/const';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { useSubmittingState } from 'hooks';
import { authModel } from 'models';
import { ROUTES } from 'routes/constants';
import { getCurrentHostAndProtocol, getQueryParams, getTWithPathKey } from 'utils';
import { ButtonComponent } from 'components';
import { COLORS } from 'theme/constants';
import { withSuccessActionsDialog } from 'utils/enhancers';
import { PasswordChecklist } from 'components/PasswordChecklist/PasswordChecklist';
import {
  Box,
  BuilderAuthCard,
  BuilderStyledButton,
  BuilderSubtitle,
  BuilderTextValidator,
  BuilderTitle,
} from 'pages/auth/style';
import { redirectToSignIn } from 'utils/helpers/urlHelpers';
import { AUTH_HEADERS } from 'models/constants';

import BuilderSignIn from '../BuilderSignIn/BuilderSignIn';

import HtmlLoader from './htmlLoader';

const PLANOGRAM_CREATING_POLL_INTERVAL = 3000;

interface BuilderSetPasswordProps {
  setSuccessActionMessages: (messages: string[]) => void;
}

function BuilderSetPassword({ setSuccessActionMessages }: BuilderSetPasswordProps) {
  const [passwordSetupSuccess, setPasswordSetupSuccess] = useState(false);
  const [state, setState] = useState({
    [authModel.fields.password]: '',
    [authModel.fields.passwordConfirmation]: '',
    [authModel.fields.token]: '',
  });
  const { handleServerError } = useServerErrorActions();
  const getT = getTWithPathKey('pages.auth.setPassword');
  const history = useHistory();
  const { withHandlingSubmittingState, isSubmitting } = useSubmittingState();

  const queryParams = useMemo(getQueryParams, []);

  const hasPasswordInToken = typeof queryParams.builder_token === 'string';

  useEffect(() => {
    if (hasPasswordInToken) {
      authModel
        .verifyBuilderEmail({ builder_token: queryParams.builder_token as string })
        .then(async data => {
          localStorage.setItem(AUTH_HEADERS.PUBTOOL_TOKEN, data.pubtoolToken);
          let attributes = await authModel.verifyToken();
          while (attributes.planogramId === null) {
            await new Promise(resolve => setTimeout(resolve, PLANOGRAM_CREATING_POLL_INTERVAL));
            attributes = await authModel.verifyToken();
          }
          window.open(`${getCurrentHostAndProtocol()}${ROUTES.curatorNamespace}${attributes.planogramId}`, '_self');
        })
        .catch(() => {
          history.push(ROUTES.linkExpired);
        });
    } else if (typeof queryParams.token === 'string') {
      authModel
        .verifyEmailToken({ token: queryParams.token })
        .then(data => {
          setState(prevState => ({
            ...prevState,
            [authModel.fields.token]: data.setPasswordToken,
          }));
        })
        .catch(() => {
          history.push(ROUTES.linkExpired);
        });
    } else {
      redirectToSignIn();
    }
  }, []);

  const handlePost = withHandlingSubmittingState(async () => {
    if (state[authModel.fields.password] !== state[authModel.fields.passwordConfirmation]) {
      return handleServerError({
        response: {
          data: {
            errors: {
              [authModel.fields.passwordConfirmation]: ['Password does not match'],
            },
          },
        },
      });
    }
    return authModel
      .setPassword({
        password: state.password,
        token: state.token,
      })
      .then(() => {
        setPasswordSetupSuccess(true);
      })
      .catch(error => {
        handleServerError(error);
      });
  });

  const handleChange = (item: Record<string, string>) =>
    setState(prevState => ({
      ...prevState,
      ...item,
    }));

  const disableResendButton = [state[authModel.fields.password], state[authModel.fields.passwordConfirmation]].some(
    value => !value,
  );

  if (hasPasswordInToken) return <HtmlLoader />;

  if (passwordSetupSuccess) {
    const BuilderSignInWithMessage = withSuccessActionsDialog(BuilderSignIn);

    return (
      <BuilderSignInWithMessage
        title="Success!"
        message="Your profile was successfully created! Please Log In to continue."
        setSuccessActionMessages={setSuccessActionMessages}
      />
    );
  }

  return (
    <BuilderAuthCard>
      <Helmet title={getT('tabTitle')} />
      <Grid container direction="column" alignItems="center" spacing={6}>
        <Grid item xl={7} style={{ flex: 1, width: '100%' }}>
          <Box mb={6}>
            <ButtonComponent
              color="primary"
              variant="text"
              className="no-padding"
              text="< Back"
              onClick={redirectToSignIn}
              style={{ color: COLORS.builderBrandColor }}
            />
          </Box>
          <BuilderTitle>Set your password</BuilderTitle>
          <Box mt={4} mb={8}>
            <BuilderSubtitle>Choose a strong password</BuilderSubtitle>
          </Box>
        </Grid>
        <Grid item xl={7} style={{ flex: 1 }}>
          <ValidatorForm onSubmit={handlePost}>
            <Box mb={3}>
              <PasswordChecklist
                open={state[authModel.fields.password].length > 0}
                password={state[authModel.fields.password]}
              >
                <BuilderTextValidator
                  validators={[VALIDATORS_MAP.required]}
                  label={getT('passwordInputLabel')}
                  value={state[authModel.fields.password]}
                  fieldName={authModel.fields.password}
                  handleChange={handleChange}
                  type="password"
                  disableMinHeight
                />
              </PasswordChecklist>
            </Box>
            <BuilderTextValidator
              validators={[VALIDATORS_MAP.required]}
              label={getT('passwordConfirmInputLabel')}
              value={state[authModel.fields.passwordConfirmation]}
              fieldName={authModel.fields.passwordConfirmation}
              handleChange={handleChange}
              type="password"
              disableMinHeight
            />
            <Box mt={5}>
              <BuilderStyledButton
                fullWidth
                color="primary"
                type="submit"
                text="Set profile password"
                disabled={disableResendButton}
                loading={isSubmitting}
              />
            </Box>
          </ValidatorForm>
        </Grid>
      </Grid>
    </BuilderAuthCard>
  );
}

export default withSuccessActionsDialog(BuilderSetPassword);
